import type { Schemas } from "@platform/contracts/client";
import {
  Button,
  Input,
  Label,
  SegmentedTabs,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
} from "@platform/ui";
import {
  ErrorText,
  errorKey,
  issuesOf,
  issueText,
  localName,
  useSession,
  useText,
} from "@platform/ui/features";
import { type FormEvent, Fragment, useCallback, useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import {
  categoryPath,
  type OpsCategory,
  type RiskListEntry,
  type RiskMatch,
  treeOrder,
} from "../lib/catalog";
import { formatDateTime, locale } from "../lib/i18n";

type ListPath = "prohibited" | "export-control";
type MatchType = Schemas["RiskMatchType"];
type Action = "block" | "keep";

const LISTS: readonly ListPath[] = ["prohibited", "export-control"];
const MATCH_TYPES: readonly MatchType[] = ["hs_prefix", "keyword", "category"];
const MIN_REASON = 3;
/** How often the page asks again while a new entry's check of the published products runs. */
const POLL_MS = 2000;
/** An entry's value as operators read it: a category by its path, anything else as written. */
const shownValue = (categories: readonly OpsCategory[], entry: { matchType: MatchType; value: string }) =>
  entry.matchType === "category" ? categoryPath(categories, entry.value) || entry.value : entry.value;

/**
 * Settling a match (card 076): blocking the product, whose reason its seller receives, or keeping it on sale, whose
 * reason goes on the product's audit trail.
 */
function ResolveForm({
  match,
  action,
  onDone,
  onCancel,
}: {
  match: RiskMatch;
  action: Action;
  onDone: () => void;
  onCancel: () => void;
}) {
  const t = useTranslations();
  const { session } = useSession();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const id = `${action}-reason-${match.id}`;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    const body = { reason: reason.trim() };
    const { error: problem, response } =
      action === "block"
        ? await session.api.POST("/v1/ops/products/{productId}/block", {
            params: { path: { productId: match.product.id } },
            body,
          })
        : await session.api.POST("/v1/ops/risk-matches/{matchId}/keep", {
            params: { path: { matchId: match.id } },
            body,
          });
    setBusy(false);
    if (!response.ok) return setError(t(errorKey(problem)));
    onDone();
  };

  return (
    <form className="flex flex-col gap-2" onSubmit={(event) => void submit(event)}>
      <Label htmlFor={id}>
        {t(action === "block" ? "admin.risk.matches.blockReason" : "admin.risk.matches.keepReason")}
      </Label>
      <Textarea id={id} value={reason} maxLength={2000} onChange={(event) => setReason(event.target.value)} />
      {error !== undefined && <ErrorText>{error}</ErrorText>}
      <span className="flex gap-2">
        <Button type="submit" size="sm" disabled={busy || reason.trim().length < MIN_REASON}>
          {t(action === "block" ? "admin.risk.matches.block" : "admin.risk.matches.keep")}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </span>
    </form>
  );
}

/**
 * Published products that new entries matched, as they are now (card 076). An open match is blocked, which needs
 * `ops:catalog.manage` as blocking anywhere does, or kept; a closed one says who closed it, and why.
 */
function MatchTable({
  matches,
  categories,
  showEntry,
  onChanged,
}: {
  matches: readonly RiskMatch[];
  categories: readonly OpsCategory[];
  showEntry: boolean;
  onChanged: () => void;
}) {
  const t = useTranslations();
  const { state } = useSession();
  const me = state.status === "signed_in" ? state.claims.sub : undefined;
  const canBlock = state.status === "signed_in" && state.claims.perm.includes("ops:catalog.manage");
  const [acting, setActing] = useState<{ id: string; action: Action } | undefined>();

  const decision = (match: RiskMatch) => {
    const resolution = match.resolution;
    if (match.state === "open" || resolution === null) return t("admin.risk.matches.open");
    if (match.state === "entry_removed") return t("admin.risk.matches.entryRemoved");
    const who = t(resolution.operatorId === me ? "admin.risk.matches.you" : "admin.risk.matches.someone");
    return t(match.state === "blocked" ? "admin.risk.matches.blockedBy" : "admin.risk.matches.keptBy", {
      who,
      reason: resolution.reason ?? "",
    });
  };

  return (
    <div className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
      <Table>
        <TableHead>
          <TableRow>
            {showEntry && <TableHeaderCell>{t("admin.risk.matches.entry")}</TableHeaderCell>}
            <TableHeaderCell>{t("admin.risk.matches.product")}</TableHeaderCell>
            <TableHeaderCell>{t("admin.risk.matches.shop")}</TableHeaderCell>
            <TableHeaderCell>{t("admin.risk.matches.productStatus")}</TableHeaderCell>
            <TableHeaderCell>{t("admin.risk.matches.found")}</TableHeaderCell>
            <TableHeaderCell>{t("admin.risk.matches.decision")}</TableHeaderCell>
            <TableHeaderCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {matches.map((match) => {
            const name = localName(match.product.name, locale);
            return (
              <TableRow key={match.id} className="align-top">
                {showEntry && (
                  <TableCell>
                    {t(`admin.risk.lists.${match.entry.listType}`)}
                    <span className="block font-medium">{shownValue(categories, match.entry)}</span>
                  </TableCell>
                )}
                <TableCell>
                  <span className="font-medium">{name}</span>
                  {match.product.slug !== null && (
                    <span className="block text-caption text-ink-muted">{match.product.slug}</span>
                  )}
                </TableCell>
                <TableCell>
                  {localName(match.product.shop.name, locale)}
                  <span className="block text-caption text-ink-muted">{match.product.shop.slug}</span>
                </TableCell>
                <TableCell>{t(`admin.products.status.${match.product.status}`)}</TableCell>
                <TableCell>{formatDateTime(match.matchedAt)}</TableCell>
                <TableCell>{decision(match)}</TableCell>
                <TableCell className="w-72">
                  {match.state === "open" &&
                    (acting?.id === match.id ? (
                      <ResolveForm
                        match={match}
                        action={acting.action}
                        onCancel={() => setActing(undefined)}
                        onDone={() => {
                          setActing(undefined);
                          onChanged();
                        }}
                      />
                    ) : (
                      <span className="flex justify-end gap-2">
                        {canBlock && match.product.status !== "blocked" && (
                          <Button
                            size="sm"
                            variant="secondary"
                            aria-label={t("admin.risk.matches.blockLabel", { product: name })}
                            onClick={() => setActing({ id: match.id, action: "block" })}
                          >
                            {t("admin.risk.matches.block")}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={t("admin.risk.matches.keepLabel", { product: name })}
                          onClick={() => setActing({ id: match.id, action: "keep" })}
                        >
                          {t("admin.risk.matches.keep")}
                        </Button>
                      </span>
                    ))}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** Every match of one entry, open first, including those closed since. */
function EntryMatches({
  list,
  entryId,
  categories,
  version,
  onChanged,
}: {
  list: ListPath;
  entryId: string;
  categories: readonly OpsCategory[];
  version: number;
  onChanged: () => void;
}) {
  const t = useTranslations();
  const { session } = useSession();
  const [matches, setMatches] = useState<readonly RiskMatch[] | undefined>();
  const [error, setError] = useState<string | undefined>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: version asks for the matches again after a change.
  useEffect(() => {
    void session.api
      .GET("/v1/ops/risk-lists/{listType}/entries/{entryId}/matches", {
        params: { path: { listType: list, entryId } },
      })
      .then(({ data, error: problem }) => {
        if (data === undefined) return setError(t(errorKey(problem)));
        setMatches(data.items);
      });
  }, [session, list, entryId, t, version]);

  if (error !== undefined) return <ErrorText>{error}</ErrorText>;
  if (matches === undefined) return null;
  if (matches.length === 0)
    return <p className="text-small text-ink-muted">{t("admin.risk.scan.nothing")}</p>;
  return <MatchTable matches={matches} categories={categories} showEntry={false} onChanged={onChanged} />;
}

/**
 * The prohibited and export-control lists (CAT-5, ADM-4, card 044): entries by HS-code prefix, keyword, or category,
 * each with a note. Adding and removing are audited by the API, and apply to submissions from then on. A new entry is
 * also checked against the published products (card 075); the matches waiting for a decision head the page, and each
 * entry shows its own (card 076).
 */
export function RiskListsPage() {
  const t = useTranslations();
  const text = useText();
  const { session } = useSession();
  const [list, setList] = useState<ListPath>("prohibited");
  const [entries, setEntries] = useState<readonly RiskListEntry[] | undefined>();
  const [categories, setCategories] = useState<readonly OpsCategory[]>([]);
  const [waiting, setWaiting] = useState<readonly RiskMatch[]>([]);
  const [shown, setShown] = useState<string | undefined>();
  const [version, setVersion] = useState(0);
  const [matchType, setMatchType] = useState<MatchType>("keyword");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [issue, setIssue] = useState<string | undefined>();
  const [removing, setRemoving] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(async () => {
    const { data, error: problem } = await session.api.GET("/v1/ops/risk-lists/{listType}/entries", {
      params: { path: { listType: list } },
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setError(undefined);
    setEntries(data.items);
  }, [session, list, t]);

  useEffect(() => {
    setEntries(undefined);
    setShown(undefined);
    void load();
  }, [load]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: version asks for the queue again after a change.
  useEffect(() => {
    void session.api
      .GET("/v1/ops/risk-matches", { params: { query: { state: "open" } } })
      .then(({ data }) => setWaiting(data?.items ?? []));
  }, [session, version]);

  useEffect(() => {
    void session.api.GET("/v1/ops/categories").then(({ data }) => setCategories(data?.items ?? []));
  }, [session]);

  // While a new entry's check runs, ask again, and refresh the queue with what it found.
  useEffect(() => {
    if (!entries?.some((entry) => entry.scan?.state === "running")) return;
    const timer = setTimeout(() => {
      void load();
      setVersion((current) => current + 1);
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [entries, load]);

  const changed = () => {
    void load();
    setVersion((current) => current + 1);
  };

  const add = async (event: FormEvent) => {
    event.preventDefault();
    setIssue(undefined);
    const { data, error: problem } = await session.api.POST("/v1/ops/risk-lists/{listType}/entries", {
      params: { path: { listType: list } },
      body: { matchType, value: value.trim(), note: note.trim() === "" ? null : note.trim() },
    });
    if (data === undefined) {
      const fields = issuesOf(problem);
      return setIssue(
        fields === undefined ? t(errorKey(problem)) : issueText(text.t, Object.values(fields)[0]),
      );
    }
    setEntries((current) => [data, ...(current ?? [])]);
    setValue("");
    setNote("");
  };

  const remove = async (entry: RiskListEntry) => {
    const { error: problem, response } = await session.api.DELETE(
      "/v1/ops/risk-lists/{listType}/entries/{entryId}",
      {
        params: { path: { listType: list, entryId: entry.id } },
      },
    );
    if (response.status !== 204) return setError(t(errorKey(problem)));
    setRemoving(undefined);
    setEntries((current) => current?.filter((each) => each.id !== entry.id));
    setVersion((current) => current + 1);
  };

  const check = (entry: RiskListEntry) => {
    const scan = entry.scan ?? null;
    if (scan === null) return <span className="text-ink-muted">{t("admin.risk.scan.none")}</span>;
    if (scan.state === "running") return <span role="status">{t("admin.risk.scan.running")}</span>;
    const open = shown === entry.id;
    return (
      <span className="flex flex-col items-start gap-1">
        {t("admin.risk.scan.done", { count: scan.openMatches })}
        <Button
          size="sm"
          variant="ghost"
          aria-expanded={open}
          aria-label={
            open
              ? t("admin.risk.scan.hide")
              : t("admin.risk.scan.showLabel", { value: shownValue(categories, entry) })
          }
          onClick={() => setShown(open ? undefined : entry.id)}
        >
          {t(open ? "admin.risk.scan.hide" : "admin.risk.scan.show")}
        </Button>
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-title font-semibold text-ink">{t("admin.risk.title")}</h1>
      {waiting.length > 0 && (
        <section className="flex flex-col gap-2" aria-labelledby="risk-waiting">
          <h2 id="risk-waiting" className="text-heading font-semibold text-ink">
            {t("admin.risk.matches.title", { count: waiting.length })}
          </h2>
          <p className="text-small text-ink-muted">{t("admin.risk.matches.intro")}</p>
          <MatchTable matches={waiting} categories={categories} showEntry onChanged={changed} />
        </section>
      )}
      <SegmentedTabs
        label={t("admin.risk.title")}
        value={list}
        onChange={(next) => setList(next as typeof list)}
        items={LISTS.map((each) => ({
          id: each,
          label: t(`admin.risk.lists.${each === "prohibited" ? "prohibited" : "export_control"}`),
        }))}
      />
      <p className="text-small text-ink-muted">{t("admin.risk.intro")}</p>
      <form
        className="flex flex-wrap items-end gap-3 rounded-(--radius-card) border border-border bg-surface p-4"
        onSubmit={(event) => void add(event)}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="risk-match-type">{t("admin.risk.matchType")}</Label>
          <Select
            id="risk-match-type"
            value={matchType}
            onChange={(event) => {
              setMatchType(event.target.value as MatchType);
              setValue("");
            }}
          >
            {MATCH_TYPES.map((each) => (
              <option key={each} value={each}>
                {t(`admin.risk.matchTypes.${each}`)}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex min-w-60 flex-col gap-1.5">
          <Label htmlFor="risk-value">{t(`admin.risk.values.${matchType}`)}</Label>
          {matchType === "category" ? (
            <Select id="risk-value" value={value} onChange={(event) => setValue(event.target.value)}>
              <option value="">{t("admin.risk.chooseCategory")}</option>
              {treeOrder(categories).map(({ category, depth }) => (
                <option key={category.id} value={category.id}>
                  {`${"— ".repeat(depth)}${localName(category.name, locale)}`}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              id="risk-value"
              value={value}
              maxLength={100}
              inputMode={matchType === "hs_prefix" ? "numeric" : undefined}
              onChange={(event) => setValue(event.target.value)}
            />
          )}
        </div>
        <div className="flex min-w-60 flex-1 flex-col gap-1.5">
          <Label htmlFor="risk-note">{t("admin.risk.note")}</Label>
          <Input
            id="risk-note"
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={value.trim() === ""}>
          {t("admin.risk.add")}
        </Button>
        {issue !== undefined && (
          <div className="w-full">
            <ErrorText>{issue}</ErrorText>
          </div>
        )}
      </form>
      {error !== undefined && <ErrorText>{error}</ErrorText>}
      {entries !== undefined && entries.length === 0 && (
        <p className="text-small text-ink-muted">{t("admin.risk.empty")}</p>
      )}
      {entries !== undefined && entries.length > 0 && (
        <div className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t("admin.risk.matchType")}</TableHeaderCell>
                <TableHeaderCell>{t("admin.risk.value")}</TableHeaderCell>
                <TableHeaderCell>{t("admin.risk.note")}</TableHeaderCell>
                <TableHeaderCell>{t("admin.risk.added")}</TableHeaderCell>
                <TableHeaderCell>{t("admin.risk.checks")}</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <Fragment key={entry.id}>
                  <TableRow className="align-top">
                    <TableCell>{t(`admin.risk.matchTypes.${entry.matchType}`)}</TableCell>
                    <TableCell className="font-medium">{shownValue(categories, entry)}</TableCell>
                    <TableCell>{entry.note ?? ""}</TableCell>
                    <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                    <TableCell>{check(entry)}</TableCell>
                    <TableCell className="text-right">
                      {removing === entry.id ? (
                        <span className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => void remove(entry)}>
                            {t("admin.risk.confirmRemove")}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setRemoving(undefined)}>
                            {t("common.cancel")}
                          </Button>
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={t("admin.risk.removeLabel", { value: shownValue(categories, entry) })}
                          onClick={() => setRemoving(entry.id)}
                        >
                          {t("admin.risk.remove")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {shown === entry.id && (
                    <TableRow>
                      <TableCell colSpan={6} className="pb-4">
                        <EntryMatches
                          list={list}
                          entryId={entry.id}
                          categories={categories}
                          version={version}
                          onChanged={changed}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
